// SmsSettings — admin form for the SMS Online GH gateway.
//
// Reads/writes /sms/settings on the backend, which persists to
// system_settings.sms.*. See backend/services/SmsService.js for the
// runtime that consumes these values.
//
// The API key is round-tripped as a masked string on GET; on PUT the
// backend only overwrites it if the client sends a value that doesn't
// contain the mask marker (••), so saving the form without touching
// the key field leaves the stored key untouched.

import React, { useEffect, useState } from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';
import {
  Box, Card, CardHeader, CardContent, Divider, Typography,
  TextField, InputAdornment, IconButton, Switch, FormControlLabel,
  Button, Stack, Alert, AlertTitle, CircularProgress, Chip,
} from '@mui/material';
import {
  Visibility, VisibilityOff, Save, Refresh, Send as SendIcon,
  AccountBalanceWallet, SmsOutlined,
} from '@mui/icons-material';
import { smsSettings, smsBalance, SmsSend } from '../BaseURL';

export default function SmsSettings() {
  const [loading, setLoading]   = useState(true);
  const [saving,  setSaving]    = useState(false);
  const [showKey, setShowKey]   = useState(false);
  const [testing, setTesting]   = useState(false);
  const [checking, setChecking] = useState(false);
  const [balance, setBalance]   = useState(null);
  const [testTo,  setTestTo]    = useState('');
  const [form, setForm] = useState({
    enabled:      true,
    api_key:      '',
    api_key_masked: '',
    sender_id:    'TOTALCARE',
    api_base:     'https://api.smsonlinegh.com/v5',
    send_path:    '/message/sms/send',
    balance_path: '/account/balance',
  });
  // Track whether the user has typed into the API-key field so we know
  // whether to send it on save (blank field → keep the stored key).
  const [keyDirty, setKeyDirty] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(smsSettings);
      if (res.data.success) {
        setForm(f => ({ ...f, ...res.data.data, api_key: '' }));
        setKeyDirty(false);
      }
    } catch (e) {
      Swal.fire({ icon: 'error', title: 'Load failed', text: e.message });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const onChange = (k) => (e) => {
    const v = e.target?.type === 'checkbox' ? e.target.checked : e.target.value;
    setForm(f => ({ ...f, [k]: v }));
    if (k === 'api_key') setKeyDirty(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        enabled:      form.enabled,
        sender_id:    form.sender_id,
        api_base:     form.api_base,
        send_path:    form.send_path,
        balance_path: form.balance_path,
      };
      if (keyDirty && form.api_key.trim()) payload.api_key = form.api_key.trim();
      const res = await axios.put(smsSettings, payload);
      if (res.data.success) {
        Swal.fire({ icon: 'success', title: 'Saved', timer: 1400, showConfirmButton: false });
        await load();
      } else {
        throw new Error(res.data.message || 'Save failed');
      }
    } catch (e) {
      Swal.fire({ icon: 'error', title: 'Save failed', text: e.message });
    } finally {
      setSaving(false);
    }
  };

  const checkBalance = async () => {
    setChecking(true); setBalance(null);
    try {
      const res = await axios.get(smsBalance);
      if (res.data.success) setBalance(res.data.data);
      else Swal.fire({ icon: 'warning', title: 'Balance check failed', text: res.data.message || 'Unknown error' });
    } catch (e) {
      Swal.fire({ icon: 'error', title: 'Balance check failed', text: e.message });
    } finally {
      setChecking(false);
    }
  };

  const sendTest = async () => {
    if (!testTo.trim()) {
      Swal.fire({ icon: 'info', title: 'Enter a phone number', text: 'e.g. 0244123456' });
      return;
    }
    setTesting(true);
    try {
      const res = await axios.post(SmsSend, {
        recipients: [testTo.trim()],
        message: `TotalCare SMS test — ${new Date().toLocaleString()}`,
        kind: 'test',
      });
      if (res.data.success && res.data.sent > 0) {
        Swal.fire({ icon: 'success', title: 'Test sent', text: `Delivered to gateway for ${testTo}` });
      } else {
        Swal.fire({ icon: 'warning', title: 'Test failed', text: `${res.data.sent || 0}/${res.data.totalRecipients || 1} sent — check the SMS log for details.` });
      }
    } catch (e) {
      Swal.fire({ icon: 'error', title: 'Test failed', text: e.message });
    } finally {
      setTesting(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  // NOTE: rendered inside the System Settings accordion in
  // Hospitaladminsettings.js. The accordion styling flattens any inner
  // Card (removes shadow/border, hides CardHeader), so we don't need
  // an outer padded Box here — the accordion body supplies the padding.
  return (
    <Box>
      <Card sx={{ borderRadius: 3, boxShadow: '0 4px 20px rgba(17,153,142,0.08)' }}>
        <CardHeader
          avatar={<Box sx={{ bgcolor: '#11998e', color: 'white', borderRadius: 2, p: 1, display: 'flex' }}><SmsOutlined /></Box>}
          title={<Typography variant="h6" fontWeight={800}>SMS Gateway Settings</Typography>}
          subheader="Configure the SMS Online GH provider. Changes take effect on the next SMS send."
          action={
            <FormControlLabel
              labelPlacement="start"
              control={<Switch checked={!!form.enabled} onChange={onChange('enabled')} />}
              label={
                <Typography variant="caption" fontWeight={800} color={form.enabled ? 'success.main' : 'text.disabled'}>
                  {form.enabled ? 'SMS ON' : 'SMS OFF'}
                </Typography>
              }
            />
          }
        />
        <Divider />
        <CardContent>
          {!form.enabled && (
            <Alert severity="warning" sx={{ mb: 2, borderRadius: 2 }}>
              <AlertTitle sx={{ fontSize: '0.85rem', fontWeight: 700 }}>Gateway disabled</AlertTitle>
              No SMS will be sent regardless of settings below. Toggle the switch above to enable.
            </Alert>
          )}

          <Stack spacing={2.2}>
            <TextField
              label="API Key"
              type={showKey ? 'text' : 'password'}
              value={keyDirty ? form.api_key : (form.api_key_masked || '')}
              onChange={onChange('api_key')}
              onFocus={() => { if (!keyDirty) { setForm(f => ({ ...f, api_key: '' })); } }}
              placeholder={form.api_key_masked ? 'Leave blank to keep the current key' : 'Paste your SMS Online GH API key'}
              helperText="Generate from your SMS Online GH portal: Settings → API Keys. Leave blank when saving to preserve the stored value."
              fullWidth
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setShowKey(v => !v)} edge="end">
                      {showKey ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <TextField
              label="Sender ID"
              value={form.sender_id}
              onChange={onChange('sender_id')}
              inputProps={{ maxLength: 11 }}
              helperText={`Max 11 characters. Must be pre-registered with SMS Online GH. (${(form.sender_id || '').length}/11)`}
              fullWidth
            />

            <TextField
              label="API Base URL"
              value={form.api_base}
              onChange={onChange('api_base')}
              helperText="Provider base URL. Rarely needs changing."
              fullWidth
            />

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField
                label="Send Path"
                value={form.send_path}
                onChange={onChange('send_path')}
                helperText="POST endpoint for outbound messages"
                fullWidth
              />
              <TextField
                label="Balance Path"
                value={form.balance_path}
                onChange={onChange('balance_path')}
                helperText="GET endpoint for credit balance"
                fullWidth
              />
            </Stack>
          </Stack>

          <Divider sx={{ my: 3 }} />

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ xs: 'stretch', sm: 'center' }}>
            <Button
              variant="contained"
              startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <Save />}
              onClick={save}
              disabled={saving}
              sx={{ bgcolor: '#11998e', '&:hover': { bgcolor: '#0d7d74' }, minWidth: 160 }}
            >
              {saving ? 'Saving…' : 'Save Settings'}
            </Button>
            <Button
              variant="outlined"
              startIcon={<Refresh />}
              onClick={load}
              disabled={loading}
            >
              Reload
            </Button>
            <Box sx={{ flex: 1 }} />
            <Button
              variant="outlined"
              startIcon={checking ? <CircularProgress size={16} /> : <AccountBalanceWallet />}
              onClick={checkBalance}
              disabled={checking}
            >
              {checking ? 'Checking…' : 'Check Balance'}
            </Button>
          </Stack>

          {balance != null && (
            <Alert severity="info" sx={{ mt: 2, borderRadius: 2 }}>
              <AlertTitle sx={{ fontSize: '0.85rem', fontWeight: 700 }}>Balance</AlertTitle>
              <Stack direction="row" spacing={1} alignItems="center">
                <Chip
                  label={balance.balance != null ? String(balance.balance) : '—'}
                  color="success" size="small" sx={{ fontWeight: 700 }}
                />
                <Typography variant="body2">{balance.currency || 'credits'}</Typography>
              </Stack>
            </Alert>
          )}

          <Divider sx={{ my: 3 }} />
          <Typography variant="subtitle2" fontWeight={800} sx={{ mb: 1 }}>Send a test SMS</Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ xs: 'stretch', sm: 'center' }}>
            <TextField
              label="Recipient phone"
              value={testTo}
              onChange={(e) => setTestTo(e.target.value)}
              placeholder="0244123456"
              size="small"
              sx={{ minWidth: 240 }}
            />
            <Button
              variant="contained"
              startIcon={testing ? <CircularProgress size={16} color="inherit" /> : <SendIcon />}
              onClick={sendTest}
              disabled={testing}
              sx={{ bgcolor: '#0d7d74', '&:hover': { bgcolor: '#0a6058' } }}
            >
              {testing ? 'Sending…' : 'Send Test'}
            </Button>
            <Typography variant="caption" color="text.secondary">
              A test row will appear in the SMS log with full provider diagnostics.
            </Typography>
          </Stack>
        </CardContent>
      </Card>
    </Box>
  );
}
